import React, { useState, useEffect, useRef } from 'react';
import { useValorePrices } from '../contexts/ValorePriceContext';
import { ALL_VALORE_SYMBOLS } from '../constants/valoreSymbols';

const ValoreTestPage: React.FC = () => {
  const { prices, connectionStatus, isConnected } = useValorePrices();
  const [logs, setLogs] = useState<string[]>([]);
  const [rawMessages, setRawMessages] = useState<string[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev.slice(0, 49)]);
  };

  useEffect(() => {
    addLog(`Connection status: ${connectionStatus}`);
  }, [connectionStatus]);

  useEffect(() => {
    addLog(`Prices map size: ${prices.size}`);
    if (prices.size > 0) {
      const firstFive = Array.from(prices.entries()).slice(0, 5);
      firstFive.forEach(([symbol, data]) => {
        addLog(`  ${symbol}: bid=${data.bid}, ask=${data.ask}, mid=${data.mid}`);
      });
    }
  }, [prices]);

  const connectDirectly = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    addLog('Attempting direct WebSocket connection...');

    try {
      const ws = new WebSocket('wss://stream.valore.markets');
      wsRef.current = ws;

      ws.onopen = () => {
        addLog('Direct WS: Connected!');
      };

      ws.onmessage = (event) => {
        addLog(`Direct WS: Received message (${event.data.length} bytes)`);
        setRawMessages(prev => [event.data.substring(0, 500), ...prev.slice(0, 9)]);

        try {
          const parsed = JSON.parse(event.data);
          addLog(`Direct WS: Message type = ${parsed.type}`);
          if (parsed.data) {
            const symbols = Object.keys(parsed.data);
            addLog(`Direct WS: Contains ${symbols.length} symbols`);
            if (symbols.length > 0) {
              addLog(`Direct WS: First symbol = ${symbols[0]}`);
            }
          }
        } catch (e) {
          addLog(`Direct WS: Failed to parse JSON`);
        }
      };

      ws.onerror = (error) => {
        addLog(`Direct WS: Error occurred`);
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        addLog(`Direct WS: Closed (code=${event.code}, reason=${event.reason})`);
      };
    } catch (error) {
      addLog(`Direct WS: Connection failed - ${error}`);
    }
  };

  const disconnectDirectly = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      addLog('Direct WS: Manually disconnected');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Valore WebSocket Test Page</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Context Status</h2>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Connection:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {connectionStatus}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Prices loaded:</span>
                <span className="font-mono">{prices.size}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Expected symbols:</span>
                <span className="font-mono">{ALL_VALORE_SYMBOLS.length}</span>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={connectDirectly}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm"
              >
                Test Direct Connection
              </button>
              <button
                onClick={disconnectDirectly}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-700 rounded text-sm"
              >
                Disconnect
              </button>
            </div>

            <h3 className="text-sm font-semibold text-slate-400 mb-2">Debug Logs</h3>
            <div className="bg-slate-900 rounded p-3 h-64 overflow-y-auto font-mono text-xs">
              {logs.map((log, i) => (
                <div key={i} className="text-slate-300">{log}</div>
              ))}
              {logs.length === 0 && (
                <div className="text-slate-500">No logs yet...</div>
              )}
            </div>
          </div>

          <div className="bg-slate-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Sample Prices from Context</h2>

            <div className="space-y-1 max-h-96 overflow-y-auto">
              {['EUR/USD', 'GBP/USD', 'USD/JPY', 'XAU/USD', 'XAG/USD'].map(symbol => {
                const priceData = prices.get(symbol);
                return (
                  <div key={symbol} className="flex items-center justify-between py-2 border-b border-slate-700">
                    <span className="font-medium">{symbol}</span>
                    <div className="text-right font-mono text-sm">
                      {priceData ? (
                        <>
                          <div className="text-green-400">Bid: {priceData.bid.toFixed(5)}</div>
                          <div className="text-red-400">Ask: {priceData.ask.toFixed(5)}</div>
                          <div className="text-slate-400">Mid: {priceData.mid.toFixed(5)}</div>
                        </>
                      ) : (
                        <span className="text-slate-500">No data</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 className="text-sm font-semibold text-slate-400 mt-6 mb-2">Raw Messages (Direct WS)</h3>
            <div className="bg-slate-900 rounded p-3 h-48 overflow-y-auto font-mono text-xs">
              {rawMessages.map((msg, i) => (
                <div key={i} className="text-slate-300 mb-2 break-all">{msg}...</div>
              ))}
              {rawMessages.length === 0 && (
                <div className="text-slate-500">Click "Test Direct Connection" to see raw messages</div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-slate-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">All Prices ({prices.size} loaded)</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 max-h-96 overflow-y-auto">
            {Array.from(prices.entries()).map(([symbol, data]) => (
              <div key={symbol} className="bg-slate-700 rounded p-2 text-xs">
                <div className="font-medium text-slate-200">{symbol}</div>
                <div className="font-mono text-slate-400">{data.mid.toFixed(5)}</div>
              </div>
            ))}
            {prices.size === 0 && (
              <div className="col-span-full text-center text-slate-500 py-8">
                No prices loaded from WebSocket
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValoreTestPage;
